import { FunctionComponent, useEffect, useMemo, useState } from 'react';
import { GeoJsonProperties, Geometry, Feature } from 'geojson';
import { useNavigate, useParams } from 'react-router-dom';
import GenericIcons from '../../components/generics/GenericIcons';
import Map from '../../components/maps/Map';
import { SimpleObject } from '../../types';
import Swal from 'sweetalert2';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import { getUncatchEndpointData, sendFileWithData } from '../../utils/request';
import { formatStringNumber } from '../../utils/utils';
import LineChart from '../../components/LineChart';
import CheckBox from '../../components/CheckBox';
import MultiSlider from '../../components/MultiSlider';
import moment from 'moment-timezone';
import '../../css/PrivateStudyResult.css';

const PrivateStudyResultPage: FunctionComponent = () => {
  const { simulacionId } = useParams();
  const navigate = useNavigate();

  const [simulacionData, setSimulacionData] = useState<{
    nombreSimulacion: string;
    descripcionSimulacion: string;
    alimentadorId: string;
    conclusion: string;
    penetracionBateriasPct?: number;
  }>({ nombreSimulacion: '', descripcionSimulacion: '', alimentadorId: '', conclusion: '' });
  const [configData, setConfigData] = useState<{
    empresa: string;
    subestacion: string;
    alimentador: string;
    demandaMaxima: number;
    demandaMinima: number;
    cantidadClientesBt: number;
    cantidadClientesMt: number;
    kilometrosTramoBt: number;
    kilometrosTramoMt: number;
    cantidadTransformadores: number;
  }>({
    empresa: '',
    subestacion: '',
    alimentador: '',
    demandaMaxima: 0,
    demandaMinima: 0,
    cantidadClientesBt: 0,
    cantidadClientesMt: 0,
    kilometrosTramoBt: 0,
    kilometrosTramoMt: 0,
    cantidadTransformadores: 0
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [state, setState] = useState({
    mapPointFeatures: [] as Feature<Geometry, GeoJsonProperties>[],
    mapLineFeatures: [] as Feature<Geometry, GeoJsonProperties>[],
    mapPolygonFeatures: [] as Feature<Geometry, GeoJsonProperties>[],
    electricFeeders: {} as SimpleObject,
    mapCenter: {} as {
      lat: number;
      lng: number;
    }
  });
  const [metrics, setMetrics] = useState<{
    loadsncsumpcabove: Array<number | null>;
    trafoncsumpc: Array<number | null>;
    linesnclngpc: Array<number | null>;
    unbalancedfases: Array<number | null>;
  }>({ loadsncsumpcabove: [], trafoncsumpc: [], linesnclngpc: [], unbalancedfases: [] });

  const [config, setConfig] = useState<{
    [key: string]: {
      name: string;
      checked: boolean;
      min: number;
      max: number;
    };
  }>({
    pvResidencial: {
      name: 'PV residencial',
      checked: false,
      min: 0,
      max: 0
    },
    batResidencial: {
      name: 'Bat. residencial',
      checked: false,
      min: 0,
      max: 0
    }
  });

  const fetchMapPoints = async (alimentadorId: string) => {
    try {
      setLoading(true);
      const allElementsMap = await getUncatchEndpointData({
        endpoint: 'mapElements/geoJSON/all',
        query: { alimentadorId }
      });
      const { pointFeatures, lineFeatures, polygonFeatures, alimentadores } = allElementsMap;

      if (
        (!pointFeatures || pointFeatures.length === 0) &&
        (!lineFeatures || lineFeatures.length === 0)
      ) {
        Swal.fire({
          icon: 'warning',
          title: 'No se encontraron lineas ni artefactos'
        });
        setLoading(false);

        return;
      }

      const localMapCenter = {} as {
        lat: number;
        lng: number;
      };

      // NOTE: Is expected that always there is more than 1 point and line
      // Always is taken the second element because is possible that the first element had uncorrect coordenates
      if (pointFeatures && pointFeatures.length > 0 && lineFeatures && lineFeatures.length > 0) {
        localMapCenter['lng'] = pointFeatures[1].geometry.coordinates[0];
        localMapCenter['lat'] = pointFeatures[1].geometry.coordinates[1];
      } else if (lineFeatures && lineFeatures.length > 0) {
        Swal.fire({
          icon: 'warning',
          title: 'No se encontraron artefactos'
        });
        localMapCenter['lng'] = lineFeatures[1].geometry.coordinates[0][0];
        localMapCenter['lat'] = lineFeatures[1].geometry.coordinates[0][1];
      } else if (pointFeatures && pointFeatures.length > 0) {
        Swal.fire({
          icon: 'warning',
          title: 'No se encontraron lineas'
        });
        localMapCenter['lng'] = pointFeatures[1].geometry.coordinates[0];
        localMapCenter['lat'] = pointFeatures[1].geometry.coordinates[1];
      }

      setState((prevState) => ({
        ...prevState,
        mapPointFeatures: pointFeatures,
        mapLineFeatures: lineFeatures,
        mapPolygonFeatures: polygonFeatures,
        electricFeeders: alimentadores,
        mapCenter: localMapCenter
      }));
      setLoading(false);
    } catch (error) {
      console.log(error);
      setLoading(false);
    }
  };

  useEffect(() => {
    const getData = async () => {
      try {
        setLoading(true);
        const simulacion = await getUncatchEndpointData({
          endpoint: 'estudios/getSimulacion',
          query: { simulacionId }
        });
        const alimentador = await getUncatchEndpointData({
          endpoint: 'estudios/getAlimentadorData',
          query: { alimentadorId: simulacion?.alimentadorId }
        });
        const metrics = await getUncatchEndpointData({
          endpoint: 'estudios/getMetrics',
          query: { simulacionId }
        });
        if (simulacion) {
          setSimulacionData(simulacion);
          const pv = simulacion?.pvResidencial;
          const pctBess =
            typeof simulacion?.penetracionBateriasPct === 'number'
              ? simulacion.penetracionBateriasPct
              : 0;
          setConfig({
            pvResidencial: {
              name: 'PV residencial',
              checked: !!pv,
              min: pv && !isNaN(pv.min) ? pv.min : 0,
              max: pv && !isNaN(pv.max) ? pv.max : 0
            },
            batResidencial: {
              name: 'Bat. residencial',
              checked: pctBess > 0,
              min: pctBess,
              max: pctBess
            }
          });
        }
        if (alimentador) {
          if (simulacion?.alimentadorId) {
            fetchMapPoints(simulacion?.alimentadorId);
          }
          setConfigData({
            empresa: alimentador?.empresaNombre,
            subestacion: alimentador?.subestacionNombre,
            alimentador: alimentador?.alimentadorNombre,
            demandaMaxima: alimentador?.demandaMaxima,
            demandaMinima: alimentador?.demandaMinima,
            cantidadClientesBt: alimentador?.cantidadClientesBt,
            cantidadClientesMt: alimentador?.cantidadClientesMt,
            kilometrosTramoBt: alimentador?.kilometrosTramoBt,
            kilometrosTramoMt: alimentador?.kilometrosTramoMt,
            cantidadTransformadores: alimentador?.cantidadTransformadores
          });
        }
        if (metrics) {
          setMetrics({
            loadsncsumpcabove: (metrics?.loadsncsumpcabove ?? []).map((obj: SimpleObject) =>
              !isNaN(obj?.promedio) ? obj?.promedio : null
            ),
            trafoncsumpc: (metrics?.trafoncsumpc ?? []).map((obj: SimpleObject) =>
              !isNaN(obj?.promedio) ? obj?.promedio : null
            ),
            linesnclngpc: (metrics?.linesnclngpc ?? []).map((obj: SimpleObject) =>
              !isNaN(obj?.promedio) ? obj?.promedio : null
            ),
            unbalancedfases: (metrics?.unbalancedfases ?? []).map((obj: SimpleObject) =>
              !isNaN(obj?.promedio) ? obj?.promedio : null
            )
          });
        }
        setLoading(false);
      } catch (error) {
        console.log(error);
        setLoading(false);
      }
    };
    getData();
  }, [simulacionId]);

  const [image, setImage] = useState<string>('');

  const hostingCapacityLineSeries = useMemo(() => {
    const hasFinitePoint = (data: Array<number | null>) =>
      data.length > 0 && data.some((v) => typeof v === 'number' && Number.isFinite(v));
    const clipToAxis = (data: Array<number | null>) => (data.length > 11 ? [] : data);
    const candidates = [
      {
        label: 'Cargas residenciales con sobretensión',
        curve: 'linear' as const,
        data: clipToAxis(metrics.loadsncsumpcabove)
      },
      {
        label: 'Longitud de líneas con sobrecorriente',
        curve: 'linear' as const,
        data: clipToAxis(metrics.linesnclngpc)
      },
      {
        label: 'Transformadores con sobrecarga',
        curve: 'linear' as const,
        data: clipToAxis(metrics.trafoncsumpc)
      },
      {
        label: 'Buses con desbalance de tensión de fase (>5 %, IEEE 112)',
        curve: 'linear' as const,
        data: clipToAxis(metrics.unbalancedfases)
      }
    ];
    return candidates.filter((s) => hasFinitePoint(s.data));
  }, [metrics]);

  const exportPDF = () => {
    const contentToExport = document.getElementById('contentToExport');
    const buttonsToHide = document.querySelectorAll('.hidden-to-export');
    const sliders = document.querySelectorAll('.MuiSlider-thumb > input');

    if (contentToExport) {
      // hide the 'hidden-to-export' class buttons so that they are not visible on the PDF
      buttonsToHide.forEach((button) => {
        const htmlButton = button as HTMLElement;
        htmlButton.style.display = 'none';
      });

      // hide the '.MuiSlider-thumb > input' class inputs so that they are not visible on the PDF
      // these inputs, although not visible on the page, are grayed out when exporting a PDF file
      sliders.forEach((input) => {
        const htmlInput = input as HTMLElement;
        htmlInput.style.display = 'none';
      });

      html2canvas(contentToExport, {
        scale: 2, // scale is increased for quality improvement
        useCORS: true // use of external images is allowed
      }).then(async (canvas) => {
        const pdf = new jsPDF('p', 'mm', 'a4');
        const imgData = canvas.toDataURL('image/png');

        // a background is created for the PDF in the same gray color (#f9f9f9) as the page.
        const backgroundColor = '#f9f9f9';
        pdf.setFillColor(backgroundColor);
        pdf.rect(0, 0, 210, 297, 'F');

        // the page view is added
        pdf.addImage(imgData, 'PNG', 15, 0, 180, 290); // 180 x 290 A4 size

        // the image obtained from the map is added on top of the previous one
        if (image) {
          pdf.addImage(image, 'PNG', 69, 116, 114, 54); //position and size to leave it in the corresponding place
        }

        const pdfBase64 = pdf.output('datauristring');
        const currentDate = moment().format('DD-MM-YYYY');
        const fileName = `${simulacionData.nombreSimulacion}_${currentDate}.pdf`;

        try {
          await sendFileWithData({
            endpoint: 'estudios/saveReport',
            dataObject: {
              fileName,
              fileContent: pdfBase64,
              simulacionId
            }
          });
        } catch (error) {
          console.error('Error en la solicitud:', error);
        }

        pdf.save(fileName);

        // the class buttons 'hidden-to-export' are displayed again.
        buttonsToHide.forEach((button) => {
          const htmlButton = button as HTMLElement;
          htmlButton.style.display = '';
        });

        sliders.forEach((input) => {
          const htmlInput = input as HTMLElement;
          htmlInput.style.display = 'block';
        });
      });
    }
  };

  return (
    <div id='contentToExport' className='private-study-result-container'>
      <div className='private-study-result-title-container vdr-page-head'>
        <div className='vdr-result-hc-heading vdr-page-head__titles'>
          <span className='vdr-eyebrow vdr-eyebrow--bar'>
            Capacidad de alojamiento · N.º {simulacionId}
          </span>
          <h2 className='private-study-result-title vdr-page-head__title'>Resultados</h2>
        </div>
        <div className='vdr-page-head__actions'>
          <button
            className='secondary-button-style vdr-result-back hidden-to-export'
            onClick={() => navigate('/private/estudios')}>
            Volver
          </button>
        </div>
      </div>
      <div className='private-study-result-content-container'>
        <div className='private-study-result-study-description-container'>
          <div>
            {loading ? (
              <h3 className='private-study-result-study-title'>Cargando...</h3>
            ) : (
              <>
                <h3 className='private-study-result-study-title'>
                  {simulacionData.nombreSimulacion}
                </h3>
                <p className='private-study-result-study-description'>
                  {simulacionData.descripcionSimulacion}
                </p>
              </>
            )}
          </div>
          <button
            className='primary-button-style private-study-result-export-btn hidden-to-export'
            disabled={loading}
            onClick={exportPDF}>
            {/* display of icons is handled by CSS */}
            <GenericIcons
              className='private-study-result-export-btn-white-icon'
              icon='filePdfIcon'
              imageStyle={{ height: '100%' }}
            />
            <GenericIcons
              className='private-study-result-export-btn-blue-icon'
              icon='filePdfIconBlue'
              imageStyle={{ height: '100%' }}
            />
            Descargar Reporte
          </button>
        </div>
      </div>
      <div className='private-study-result-content-container'>
        <h3 className='private-study-result-subtitle'>Configuración</h3>
        <div className='private-study-result-config-data-container'>
          <h4 className='private-study-result-config-label-text'>
            Empresa:{' '}
            <span className='private-study-result-config-value-text'>{configData.empresa}</span>
          </h4>
          <h4 className='private-study-result-config-label-text'>
            Subestación:{' '}
            <span className='private-study-result-config-value-text'>{configData.subestacion}</span>
          </h4>
          <h4 className='private-study-result-config-label-text'>
            Nombre Alimentador:{' '}
            <span className='private-study-result-config-value-text'>{configData.alimentador}</span>
          </h4>
          <h4 className='private-study-result-config-label-text'>
            Demanda Máxima:{' '}
            <span className='private-study-result-config-value-text'>
              {formatStringNumber(configData.demandaMaxima.toString(), 0, 0)} kW
            </span>
          </h4>
          <h4 className='private-study-result-config-label-text'>
            Demanda Mínima:{' '}
            <span className='private-study-result-config-value-text'>
              {formatStringNumber(configData.demandaMinima.toString(), 0, 0)} kW
            </span>
          </h4>
        </div>
        <h3 className='private-study-result-subtitle'>Escenarios</h3>
        <h4 className='private-study-result-config-label-text'>
          Penetración BESS (% sobre cargas con PV):{' '}
          <span className='private-study-result-config-value-text'>
            {typeof simulacionData?.penetracionBateriasPct === 'number'
              ? simulacionData.penetracionBateriasPct
              : 0}
            %
          </span>
        </h4>
        <div className='private-study-result-scenario-container'>
          {Object.keys(config).map((key, idx) => (
            <div key={idx} className='private-study-result-scenario-config-container'>
              <div className='private-study-result-scenario-form-config-container'>
                <CheckBox label={config[key].name} checked={config[key].checked} disabled />
              </div>
              <div className='private-study-result-scenario-form-config-container'>
                {key === 'batResidencial' ? (
                  <MultiSlider
                    singleThumb
                    value={config[key].min}
                    disabled
                  />
                ) : (
                  <MultiSlider value={[config[key].min, config[key].max]} disabled />
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className='private-study-result-content-container'>
        <h3 className='private-study-result-subtitle'>Datos del alimentador</h3>
        <div className='private-study-result-feeder-data-container'>
          <div className='private-study-result-feeder-table'>
            <table className='private-study-result-table'>
              <thead>
                <tr>
                  <th>Parámetro</th>
                  <th>Valor</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td># de Clientes MT</td>
                  <td>{formatStringNumber(configData.cantidadClientesMt.toString(), 0, 0)}</td>
                </tr>
                <tr>
                  <td># de Clientes BT</td>
                  <td>{formatStringNumber(configData.cantidadClientesBt.toString(), 0, 0)}</td>
                </tr>
                <tr>
                  <td>Km de líneas MT</td>
                  <td>{formatStringNumber(configData.kilometrosTramoMt.toString(), 0, 0)} km</td>
                </tr>
                <tr>
                  <td>Km de líneas BT</td>
                  <td>{formatStringNumber(configData.kilometrosTramoBt.toString(), 0, 0)} km</td>
                </tr>
                <tr>
                  <td>Número transformadores</td>
                  <td>{formatStringNumber(configData.cantidadTransformadores.toString(), 0, 0)}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div className='private-study-result-map-container'>
            <Map
              mapPointFeatures={state.mapPointFeatures}
              mapLineFeatures={state.mapLineFeatures}
              mapPolygonFeatures={state.mapPolygonFeatures}
              electricFeeders={state.electricFeeders}
              mapCenter={state.mapCenter}
              exportImage={(image) => {
                setImage(image);
              }}
            />
            {loading && (
              <div className='private-study-result-map-loader-container'>
                <span className='loader'></span>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className='private-study-result-content-container'>
        <h3 className='private-study-result-subtitle'>Resultados de capacidad de alojamiento</h3>
        <div className='private-study-result-accommodation-capacity-container'>
          <div className='private-study-result-accommodation-capacity-text-container'>
            <p>{simulacionData.conclusion}</p>
          </div>
          <div className='private-study-result-accommodation-capacity-chart-container'>
            <h3 className='private-study-result-accommodation-capacity-chart-title'>
              Elementos de red con problemas
            </h3>
            {hostingCapacityLineSeries.length > 0 ? (
              <LineChart
                height={450}
                xAxis={[
                  {
                    min: 0,
                    label: 'Penetración PV (% clientes con sistema PV)',
                    data: [0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100]
                  }
                ]}
                yAxis={[
                  {
                    min: -10,
                    label: 'Porcentaje (%)',
                    data: [0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100]
                  }
                ]}
                series={hostingCapacityLineSeries}
              />
            ) : (
              <p>No hay curvas de resultados disponibles para este estudio.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivateStudyResultPage;
